import React, { useCallback, useEffect, useState } from 'react';
import { Edit2, Layers, Loader2, Plus, RefreshCw, Search, Store, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { alianzasApi, apiErrorMessage } from '../../services/alianzasApi';
import {
  ACCENT, BTN_PRIMARY, CARD, PAGE_BG, ConfirmDialog, Field, ModalShell, Toast,
  fmtFecha, inputCls, selectCls,
} from '../../components/alianzas/ui';

const PAGE_SIZE = 25;
const FABRICANTES = ['AWS', 'Microsoft', 'Google', 'Alibaba', 'Oracle', 'IBM'];
const ESTATUS = ['On Going', 'TBD', 'Q1', 'Q2', 'Q3', 'Q4'];
const REALIZADO_POR = [
  'Alianzas y Sales Specialist',
  'MKT, Alianzas y Sales Specialist',
  'MKT y Sales Specialist',
];
const MONEDAS = ['USD', 'MXN'];

const SECCIONES = {
  eventos: {
    label: 'Eventos',
    singular: 'evento',
    idField: 'evento_id',
    icon: Layers,
    fields: [
      { name: 'fabricante', label: 'Fabricante', type: 'select', options: FABRICANTES, required: true },
      { name: 'nombre_evento', label: 'Nombre del evento', required: true, wide: true },
      { name: 'topic_foco', label: 'Topic / Foco', wide: true },
      { name: 'fecha', label: 'Fecha', type: 'date' },
      { name: 'estatus', label: 'Estatus', type: 'select', options: ESTATUS },
      { name: 'lista_asistentes', label: 'Lista de asistentes', type: 'textarea', wide: true },
      { name: 'numero_asistentes', label: 'Número de asistentes', type: 'number' },
      { name: 'realizado_por', label: 'Realizado por', type: 'select', options: REALIZADO_POR },
      { name: 'fondos_inversion', label: 'Fondos de inversión', type: 'number', step: '0.01' },
      { name: 'moneda', label: 'Moneda', type: 'select', options: MONEDAS, required: true, defaultValue: 'MXN' },
      { name: 'reclamado', label: 'Reclamado', type: 'checkbox' },
      { name: 'fecha_reclamacion', label: 'Fecha de reclamación', type: 'date' },
      { name: 'notas', label: 'Notas', type: 'textarea', wide: true },
    ],
    columns: [
      { key: 'fabricante', label: 'Fabricante' },
      { key: 'nombre_evento', label: 'Nombre del evento' },
      { key: 'topic_foco', label: 'Topic / Foco' },
      { key: 'fecha', label: 'Fecha', type: 'date' },
      { key: 'estatus', label: 'Estatus' },
      { key: 'numero_asistentes', label: 'Asistentes' },
      { key: 'fondos_inversion', label: 'Fondos', type: 'currency' },
      { key: 'reclamado', label: 'Reclamado', type: 'boolean' },
    ],
  },
  marketplaces: {
    label: 'Marketplaces',
    singular: 'registro de marketplace',
    idField: 'marketplace_id',
    icon: Store,
    fields: [
      { name: 'fabricante', label: 'Fabricante', type: 'select', options: FABRICANTES, required: true },
      { name: 'ofertas_actuales', label: 'Ofertas actuales', type: 'textarea', wide: true },
      { name: 'estatus', label: 'Estatus', type: 'select', options: ESTATUS },
      { name: 'link_publicacion', label: 'Link de publicación', type: 'url', wide: true },
      { name: 'fecha_publicacion', label: 'Fecha de publicación', type: 'date' },
      { name: 'proximas_ofertas', label: 'Próximas ofertas', type: 'textarea', wide: true },
      { name: 'fecha_publicacion_ofertas_proximas', label: 'Fecha de publicación de ofertas próximas', type: 'date', wide: true },
    ],
    columns: [
      { key: 'fabricante', label: 'Fabricante' },
      { key: 'ofertas_actuales', label: 'Ofertas actuales' },
      { key: 'estatus', label: 'Estatus' },
      { key: 'link_publicacion', label: 'Publicación', type: 'url' },
      { key: 'fecha_publicacion', label: 'Fecha', type: 'date' },
      { key: 'proximas_ofertas', label: 'Próximas ofertas' },
      { key: 'fecha_publicacion_ofertas_proximas', label: 'Próxima publicación', type: 'date' },
    ],
  },
  especializaciones: {
    label: 'Especializaciones',
    singular: 'especialización',
    idField: 'especializacion_id',
    icon: Layers,
    fields: [
      { name: 'fabricante', label: 'Fabricante', type: 'select', options: FABRICANTES, required: true },
      { name: 'tipo', label: 'Tipo', type: 'select', options: ['Designación', 'Especialización', 'Competencia'], required: true },
      { name: 'nombre', label: 'Nombre', required: true, wide: true },
      { name: 'fecha_obtencion', label: 'Fecha de obtención', type: 'date' },
      { name: 'beneficios', label: 'Beneficios', type: 'textarea', wide: true },
      { name: 'requiere_auditoria', label: 'Requiere auditoría', type: 'checkbox' },
      { name: 'fecha_auditoria', label: 'Fecha de auditoría', type: 'date' },
      { name: 'costo_auditoria', label: 'Costo de auditoría', type: 'number', step: '0.01' },
      { name: 'requiere_renovacion', label: 'Requiere renovación', type: 'checkbox' },
      { name: 'fecha_renovacion', label: 'Fecha de renovación', type: 'date' },
      { name: 'costo_renovacion', label: 'Costo de renovación', type: 'number', step: '0.01' },
      { name: 'proveedor_auditoria', label: 'Proveedor de auditoría', wide: true },
    ],
    columns: [
      { key: 'fabricante', label: 'Fabricante' },
      { key: 'tipo', label: 'Tipo' },
      { key: 'nombre', label: 'Nombre' },
      { key: 'fecha_obtencion', label: 'Fecha de obtención', type: 'date' },
      { key: 'requiere_auditoria', label: 'Auditoría', type: 'boolean' },
      { key: 'fecha_auditoria', label: 'Fecha auditoría', type: 'date' },
      { key: 'costo_auditoria', label: 'Costo auditoría', type: 'currency' },
      { key: 'requiere_renovacion', label: 'Renovación', type: 'boolean' },
      { key: 'fecha_renovacion', label: 'Fecha renovación', type: 'date' },
      { key: 'costo_renovacion', label: 'Costo renovación', type: 'currency' },
      { key: 'proveedor_auditoria', label: 'Proveedor de auditoría' },
    ],
  },
};

function valorInicial(field, record) {
  const value = record?.[field.name];
  if (field.type === 'checkbox') return Boolean(value);
  if (field.type === 'date') return value ? String(value).slice(0, 10) : '';
  return value ?? field.defaultValue ?? '';
}

function RegistroModal({ entidad, record, onClose, onSave }) {
  const config = SECCIONES[entidad];
  const [form, setForm] = useState(() => Object.fromEntries(
    config.fields.map(field => [field.name, valorInicial(field, record)])
  ));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    const payload = { ...form };
    config.fields.forEach(field => {
      if (field.type === 'number') payload[field.name] = payload[field.name] === '' ? null : Number(payload[field.name]);
      if (field.type === 'date' && !payload[field.name]) payload[field.name] = null;
      if (typeof payload[field.name] === 'string' && !payload[field.name].trim()) payload[field.name] = null;
    });
    try {
      await onSave(payload);
    } catch (saveError) {
      setError(apiErrorMessage(saveError, `No se pudo guardar el ${config.singular}`));
    } finally {
      setSaving(false);
    }
  };

  const fieldControl = field => {
    const value = form[field.name];
    const onChange = event => setForm(current => ({
      ...current,
      [field.name]: field.type === 'checkbox' ? event.target.checked : event.target.value,
    }));
    if (field.type === 'select') {
      return (
        <select className={selectCls} value={value} onChange={onChange} required={field.required}>
          <option value="">Selecciona…</option>
          {field.options.map(option => <option key={option} value={option}>{option}</option>)}
        </select>
      );
    }
    if (field.type === 'checkbox') {
      return (
        <label className="flex items-center gap-2 text-sm text-white/75">
          <input type="checkbox" checked={value} onChange={onChange} className="accent-sky-400 w-4 h-4" />
          {value ? 'Sí' : 'No'}
        </label>
      );
    }
    if (field.type === 'textarea') {
      return <textarea rows={3} className={`${inputCls} resize-y`} value={value} onChange={onChange} />;
    }
    return (
      <input
        type={field.type || 'text'}
        step={field.step}
        required={field.required}
        placeholder={field.hint}
        className={inputCls}
        value={value}
        onChange={onChange}
      />
    );
  };

  return (
    <ModalShell
      title={`${record ? 'Editar' : 'Nuevo'} · ${config.singular}`}
      icon={<config.icon size={18} style={{ color: ACCENT }} />}
      onClose={onClose}
      wide
    >
      <form onSubmit={submit} className="max-h-[75vh] overflow-y-auto">
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {config.fields.map(field => (
            <div key={field.name} className={field.wide ? 'md:col-span-2' : ''}>
              <Field label={field.label} required={field.required}>
                {fieldControl(field)}
              </Field>
            </div>
          ))}
        </div>
        {error && <p className="px-6 pb-2 text-sm text-red-300">{error}</p>}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-sky-400/15">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-white/15 text-white/60">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50" style={BTN_PRIMARY}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function celda(record, column) {
  const value = record[column.key];
  if (value == null || value === '') return '—';
  if (column.type === 'date') return fmtFecha(value);
  if (column.type === 'boolean') return value ? 'Sí' : 'No';
  if (column.type === 'currency') {
    const currency = record.moneda || 'MXN';
    try {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(Number(value));
    } catch {
      return `${value} ${currency}`;
    }
  }
  if (column.type === 'url') {
    return (
      <a href={value} target="_blank" rel="noopener noreferrer" className="text-sky-300 hover:text-sky-200 underline">
        Ver publicación
      </a>
    );
  }
  return String(value);
}

export default function QBRManagementPage() {
  const { hasPermission } = useAuth();
  const [entidad, setEntidad] = useState('eventos');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [fabricante, setFabricante] = useState('');
  const [estatus, setEstatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  const canCreate = hasPermission('alianzas.qbr.gestion.create');
  const canEdit = hasPermission('alianzas.qbr.gestion.edit');
  const canDelete = hasPermission('alianzas.qbr.gestion.delete');
  const config = SECCIONES[entidad];
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasStatus = config.fields.some(field => field.name === 'estatus');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await alianzasApi.listQbr(entidad, {
        limit: PAGE_SIZE, offset, search: search || undefined,
        fabricante: fabricante || undefined, estatus: estatus || undefined,
      });
      setRows(result.data || []);
      setTotal(result.total || 0);
    } catch (loadError) {
      setError(apiErrorMessage(loadError, 'No se pudieron cargar los registros QBR'));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [entidad, offset, search, fabricante, estatus]);

  useEffect(() => { load(); }, [load]);

  const cambiarEntidad = id => {
    setEntidad(id);
    setOffset(0);
    setSearch('');
    setFabricante('');
    setEstatus('');
  };

  const guardar = async payload => {
    if (modal.record) await alianzasApi.updateQbr(entidad, modal.record[config.idField], payload);
    else await alianzasApi.createQbr(entidad, payload);
    setModal(null);
    setToast({ msg: `${config.label} guardado`, type: 'ok' });
    if (offset === 0) load();
    else setOffset(0);
  };

  const eliminar = async () => {
    setDeleting(true);
    try {
      await alianzasApi.deleteQbr(entidad, recordToDelete[config.idField]);
      setRecordToDelete(null);
      setToast({ msg: `${config.label} eliminado`, type: 'ok' });
      if (offset === 0) load();
      else setOffset(0);
    } catch (deleteError) {
      setToast({ msg: apiErrorMessage(deleteError, 'No se pudo eliminar el registro'), type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 space-y-6 min-h-screen" style={{ background: PAGE_BG, backgroundAttachment: 'fixed' }}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Layers size={22} style={{ color: ACCENT }} /> Gestión QBR
          </h1>
          <p className="text-sm text-white/50 mt-1">Eventos, marketplaces y especializaciones de fabricantes</p>
        </div>
        <Link to="/alianzas/qbr" className="px-3 py-2 rounded-lg text-sm border border-sky-400/20 text-sky-300 hover:bg-sky-400/10">
          Dashboard QBR
        </Link>
      </header>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Tablas QBR">
        {Object.entries(SECCIONES).map(([id, section]) => {
          const Icon = section.icon;
          const active = entidad === id;
          return (
            <button
              key={id}
              role="tab"
              aria-selected={active}
              onClick={() => cambiarEntidad(id)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-colors"
              style={{
                background: active ? 'rgba(0,184,255,0.16)' : 'rgba(255,255,255,0.04)',
                borderColor: active ? 'rgba(0,184,255,0.4)' : 'rgba(255,255,255,0.08)',
                color: active ? ACCENT : 'rgba(255,255,255,0.6)',
              }}
            >
              <Icon size={15} /> {section.label}
            </button>
          );
        })}
      </div>

      <section className={`${CARD} p-5`}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-white">{config.label} <span className="text-sm text-white/40">{total}</span></h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                className={`${inputCls} pl-8 w-56`}
                placeholder="Buscar…"
                value={search}
                onChange={event => { setSearch(event.target.value); setOffset(0); }}
              />
            </div>
            <select className={selectCls} value={fabricante} onChange={event => { setFabricante(event.target.value); setOffset(0); }}>
              <option value="">Todos los fabricantes</option>
              {FABRICANTES.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
            {hasStatus && (
              <select className={selectCls} value={estatus} onChange={event => { setEstatus(event.target.value); setOffset(0); }}>
                <option value="">Todos los estados</option>
                {ESTATUS.map(option => <option key={option} value={option}>{option}</option>)}
              </select>
            )}
            <button onClick={load} title="Actualizar" className="p-2 rounded-lg border border-white/10 text-white/50 hover:text-white">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            {canCreate && (
              <button onClick={() => setModal({ record: null })} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold" style={BTN_PRIMARY}>
                <Plus size={14} /> Nuevo
              </button>
            )}
          </div>
        </div>

        {error && <div className="mb-3 p-3 rounded-lg border border-red-400/20 bg-red-400/5 text-sm text-red-300">{error}</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-white/40 border-b border-white/10">
                {config.columns.map(column => <th key={column.key} className="py-2 px-3 font-medium">{column.label}</th>)}
                <th className="py-2 px-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={config.columns.length + 1} className="py-8 text-center text-white/40"><Loader2 size={18} className="inline animate-spin mr-2" />Cargando…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={config.columns.length + 1} className="py-8 text-center text-white/40">No hay registros para mostrar.</td></tr>}
              {!loading && rows.map(record => (
                <tr key={record[config.idField]} className="border-b border-white/5 hover:bg-white/[0.03]">
                  {config.columns.map(column => (
                    <td key={column.key} className="py-3 px-3 text-white/70 max-w-[260px] truncate" title={record[column.key] == null ? '' : String(record[column.key])}>
                      {celda(record, column)}
                    </td>
                  ))}
                  <td className="py-3 px-3">
                    <div className="flex justify-end gap-2">
                      {canEdit && <button onClick={() => setModal({ record })} title="Editar" className="p-1.5 text-white/40 hover:text-sky-300"><Edit2 size={14} /></button>}
                      {canDelete && <button onClick={() => setRecordToDelete(record)} title="Eliminar" className="p-1.5 text-white/40 hover:text-red-300"><Trash2 size={14} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-between mt-4 text-xs text-white/50">
            <span>Página {page} de {pages} · {total} registros</span>
            <div className="flex gap-2">
              <button disabled={offset === 0} onClick={() => setOffset(value => Math.max(0, value - PAGE_SIZE))} className="px-3 py-1.5 rounded-lg border border-white/10 disabled:opacity-30">Anterior</button>
              <button disabled={page >= pages} onClick={() => setOffset(value => value + PAGE_SIZE)} className="px-3 py-1.5 rounded-lg border border-white/10 disabled:opacity-30">Siguiente</button>
            </div>
          </div>
        )}
      </section>

      {modal && <RegistroModal entidad={entidad} record={modal.record} onClose={() => setModal(null)} onSave={guardar} />}
      {recordToDelete && (
        <ConfirmDialog
          title="Eliminar registro QBR"
          message={`¿Enviar ${recordToDelete.nombre_evento || recordToDelete.nombre || recordToDelete.fabricante} a la papelera?`}
          confirmLabel="Eliminar"
          busy={deleting}
          onClose={() => setRecordToDelete(null)}
          onConfirm={eliminar}
        />
      )}
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}